import { DragDropContext, Draggable, DraggableProvided, DraggableStateSnapshot, Droppable, DropResult } from '@hello-pangea/dnd'
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { GripVertical, RefreshCw } from 'lucide-react'
import { FC, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

type ColumnConfig = {
    field: string
    label: ReactNode | FC<{ datas: ReadonlyArray<unknown>; extras: unknown }>
    isVisible: boolean
    isLocked?: boolean
}

type ColumnConfigurationItemProps = {
    draggableProvided: DraggableProvided
    draggableSnapshot: DraggableStateSnapshot
    config: ColumnConfig
    isDraggable: boolean
    onCheckboxChange: (field: string, isCurrentlyVisible: boolean) => void
}

const ColumnConfigurationItem: FC<ColumnConfigurationItemProps> = ({
    draggableProvided,
    draggableSnapshot,
    config,
    isDraggable,
    onCheckboxChange,
}) => (
    <Box
        ref={draggableProvided.innerRef}
        {...draggableProvided.draggableProps}
        {...draggableProvided.dragHandleProps}
        sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            cursor: isDraggable ? 'grab' : 'default',
            opacity: draggableSnapshot.isDragging ? 0.5 : 1,
            backgroundColor: draggableSnapshot.isDragging ? 'action.hover' : 'transparent',
            transition: 'background-color 0.2s',
            borderBottom: 1,
            borderColor: 'divider',

            '&:active': {
                cursor: isDraggable ? 'grabbing' : 'default',
            },
        }}
    >
        <C1Icon icon={GripVertical} sx={{ mr: 1, color: isDraggable ? 'text.secondary' : 'transparent' }} />
        <FormControlLabel
            control={
                <Checkbox
                    checked={config.isVisible}
                    onChange={(): void => onCheckboxChange(config.field, config.isVisible)}
                />
            }
            sx={{ flex: 1, m: 0 }}
            label={<Typography
                component="span"
                sx={{ color: config.isVisible ? 'text.primary' : 'text.secondary' }}
            >
                {typeof config.label === 'function'
                    ? config.field
                    : config.label || config.field}
            </Typography>}
        />
    </Box>
)

export type CustomColumnConfigurationUIProps<T, ExtraProps> = {
    columns: C1TableReduxColumn<T, ExtraProps>[]
    selectedKeys: string[]
    onConfigChange: (newSelectedKeys: string[]) => void
    onReset: () => void
    hasCustomConfig: boolean
}

export const CustomColumnConfigurationUI = <T, ExtraProps = object>({
    columns,
    selectedKeys,
    onConfigChange,
    onReset,
    hasCustomConfig,
}: CustomColumnConfigurationUIProps<T, ExtraProps>): React.ReactElement => {
    // Only show customizable columns (exclude locked columns)
    const customizableColumns = columns.filter((col) => !col.isLocked)

    // Build column configurations, separating visible and hidden customizable columns
    const visibleConfigs: ColumnConfig[] = selectedKeys
        .map((key) => {
            const col = customizableColumns.find((c) => c.field === key)
            return col ? { field: col.field, label: col.label as ColumnConfig['label'], isVisible: true } : null
        })
        .filter((c): c is ColumnConfig => c !== null)

    const hiddenConfigs: ColumnConfig[] = customizableColumns
        .filter((col) => !selectedKeys.includes(col.field))
        .map((col) => ({ field: col.field, label: col.label as ColumnConfig['label'], isVisible: false }))

    const allConfigs: ColumnConfig[] = [...visibleConfigs, ...hiddenConfigs]

    const handleDragEnd = (result: DropResult): void => {
        if (!result.destination) return

        const sourceIndex = result.source.index
        const destinationIndex = result.destination.index

        if (sourceIndex === destinationIndex) return

        // Check if source or destination is hidden
        const sourceConfig = allConfigs[sourceIndex]
        const destConfig = allConfigs[destinationIndex]

        if (!sourceConfig.isVisible || !destConfig.isVisible) return

        const newConfigs = Array.from(allConfigs)
        const [removed] = newConfigs.splice(sourceIndex, 1)
        newConfigs.splice(destinationIndex, 0, removed)

        // Get visible column keys in new order
        const newSelectedKeys = newConfigs
            .filter((c) => c.isVisible)
            .map((c) => c.field)

        onConfigChange(newSelectedKeys)
    }

    const handleCheckboxChange = (field: string, isCurrentlyVisible: boolean): void => {
        if (isCurrentlyVisible) {
            // Unchecking - remove from selected
            const newSelectedKeys = selectedKeys.filter((key) => key !== field)
            onConfigChange(newSelectedKeys)
        } else {
            // Checking - add to end of selected (moves to bottom of visible columns)
            const newSelectedKeys = [...selectedKeys, field]
            onConfigChange(newSelectedKeys)
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                py: 1,
                px: 2,
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
            }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="h6">
                        <FormattedMessage
                            id="component_table_column_customization_title"
                            defaultMessage="Configure columns"
                        />
                    </Typography>
                    <Button
                        onClick={onReset}
                        variant="text"
                        size="small"
                        startIcon={<C1Icon icon={RefreshCw} />}
                        sx={{ opacity: hasCustomConfig ? 1 : 0, pointerEvents: hasCustomConfig ? 'auto' : 'none' }}
                    >
                        <FormattedMessage
                            id="component_table_column_customization_reset"
                            defaultMessage="Reset"
                        />
                    </Button>
                </Stack>
            </Box>
            <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="column-list">
                        {(provided): React.ReactElement => <Stack
                            spacing={0}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {allConfigs.map((config, index) => <Draggable
                                key={config.field}
                                draggableId={config.field}
                                index={index}
                                isDragDisabled={!config.isVisible}
                            >
                                {(draggableProvided, draggableSnapshot): React.ReactElement => <ColumnConfigurationItem
                                    draggableProvided={draggableProvided}
                                    draggableSnapshot={draggableSnapshot}
                                    config={config}
                                    isDraggable={config.isVisible}
                                    onCheckboxChange={handleCheckboxChange}
                                />}
                            </Draggable>)}
                            {provided.placeholder}
                        </Stack>}
                    </Droppable>
                </DragDropContext>
            </Box>
        </Box>
    )
}
